import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthenticationService } from '../../core/services/auth.service';
import { login, loginSuccess, loginFailure, logout, logoutSuccess, Register, RegisterSuccess, RegisterFailure, forgotPassword, forgotPasswordFailure, forgotPasswordSuccess, resetPassword, resetPasswordSuccess, resetPasswordFailure, changePassword, changePasswordSuccess, changePasswordFailure, Onboarding, OnboardingSuccess, OnboardingFailure, updateUserProfile, updateUserProfileFailure, updateUserProfileSuccess } from './authentication.actions';
import { ToastifyService } from '../../shared/toastify-service';
import { Router } from '@angular/router';
import { TeamService } from '../../core/services/team.service';
import { GenericService } from '../../core/services/generic.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';

@Injectable()
export class AuthenticationEffects {

  Register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Register),
      exhaustMap(({ email, password, name, encryptedemail, encryptedname }) => {
        return this.AuthenticationService.register(email, password, name, encryptedemail, encryptedname).pipe(
          map((user) => {
            return RegisterSuccess();
          }),
          catchError((error) => of(RegisterFailure({ error })))
        );
      })
    )
  );

  registerDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RegisterSuccess),
        tap((user: any) => {
          this.router.navigate(['/auth/success-msg/cover']);
        })
      ),
    { dispatch: false }
  );

  registerDataFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RegisterFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message);
        })
      ),
    { dispatch: false }
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      exhaustMap(({ email, password }) => {
        return this.AuthenticationService.login(email, password).pipe(
          map((user: any) => {
            let result = {"user":user, "password": password};
            return loginSuccess({ result: result });
          }),
          catchError((error) => of(loginFailure({ error })), // Closing parenthesis added here
          ));
      })
    )
  );

  loginDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess),
        tap((result: any) => {
          console.log(result);
          let userresult = result.result.user;

          //Update hardlogout key
          this.afs.collection("hardlogout").doc(userresult.user?.guid).update({
            hardlogout: false
          }).then(() => {
            console.log("Document updated successfully.");
          }).catch(error => {
            console.error("Error updating document: ", error);
          });
          sessionStorage.setItem('token', userresult.user.jwt);
          console.log("userresult", userresult);
          sessionStorage.setItem('balance', userresult.user.walletbalance ? userresult.user.walletbalance : 0);
          sessionStorage.setItem('currentUser', JSON.stringify(userresult));
          sessionStorage.setItem('activetab', "" + 1);
          if (userresult.user.isdefaultpassword) {
            this.router.navigate(['/auth/resetpassword/cover'], {queryParams: {code: userresult.user.resetPasswordToken! }});
          } else if (!userresult.user.userprofile.isonboardingcompleted && userresult.user?.role?.type == 'super_admin') {
            if (userresult.user.usertype == 'vendor') {
              this.router.navigate(['/auth/onboardingvendor/cover']);
            } else if (userresult.user.usertype == 'client') {
              this.router.navigate(['/auth/onboardingclient/cover']);
            } else if (userresult.user.usertype == 'stampingpartner') {
              this.router.navigate(['/auth/onboardingstampingpartner/cover']);
            } else {
              this.router.navigate(['/']);
            }
          } else {
            this.router.navigate(['/']);
          }

          this.genericService.requestPermission();
          this.genericService.getMessages();
        })
      ),
    { dispatch: false }
  );

  loginDataFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message);
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      tap(() => {
        // Perform any necessary cleanup or side effects before logging out
        sessionStorage.removeItem('currentUser');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('activetab');
        sessionStorage.removeItem('balance');
      }),
      exhaustMap(() => of(logoutSuccess()))
    )
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forgotPassword),
      exhaustMap(({ email }) => {
        return this.AuthenticationService.forgotPassword(email).pipe(
          map((success: any) => {
            return forgotPasswordSuccess({ success });
          }),
          catchError((error) => of(forgotPasswordFailure({ error })), // Closing parenthesis added here
          ));
      })
    )
  );

  forgotpasswordDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(forgotPasswordSuccess),
        tap((user: any) => {
          this.toastifyService.showSuccessMessage("Email has been sent successfully. Please proceed via Email.");
          this.router.navigate(['/auth/login']);
        })
      ),
    { dispatch: false }
  );

  forgotpasswordDataFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(forgotPasswordFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message);
        })
      ),
    { dispatch: false }
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPassword),
      exhaustMap(({ password, confirmpassword, code }) => {
        return this.AuthenticationService.resetPassword(password, confirmpassword, code).pipe(
          map((success: any) => {
            return resetPasswordSuccess({ success });
          }),
          catchError((error) => of(resetPasswordFailure({ error })), // Closing parenthesis added here
          ));
      })
    )
  );

  resetpasswordDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetPasswordSuccess),
        tap((user: any) => {
          this.toastifyService.showSuccessMessage("Your new password has been updated successfully. Please login with your new one.");
          this.router.navigate(['/auth/login']);
        })
      ),
    { dispatch: false }
  );

  resetpasswordDataFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetPasswordFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message);
        })
      ),
    { dispatch: false }
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePassword),
      exhaustMap(({ password, currentpassword }) => {
        return this.AuthenticationService.changePassword(password, currentpassword).pipe(
          map((success: any) => {
            return changePasswordSuccess({ success });
          }),
          catchError((error) => of(changePasswordFailure({ error })), // Closing parenthesis added here
          ));
      })
    )
  );

  changepasswordDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changePasswordSuccess),
        tap((user: any) => {
          this.toastifyService.showSuccessMessage("Password updated successfully. Please login with your new password.");
          this.AuthenticationService.logout();
          this.router.navigate(['/auth/login']);
        })
      ),
    { dispatch: false }
  );

  changepasswordDataFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changePasswordFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message);
        })
      ),
    { dispatch: false }
  );

  //Complete Onboarding
  onboarding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Onboarding),
      exhaustMap(({ company, address, userguid, oprstates, encryptedcompany, encryptedaddress }) => {
        return this.AuthenticationService.vendoronboardingComplete(company, address, userguid, oprstates, encryptedcompany, encryptedaddress).pipe(
          map((user: any) => {
            return OnboardingSuccess(user);
          }),
          catchError((error) => of(OnboardingFailure({ error })), // Closing parenthesis added here
          ));
      })
    )
  );

  onboardingDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingSuccess),
        tap((user: any) => {
          this.toastifyService.showSuccessMessage("Onboarding completed successfully.");
        })
      ),
    { dispatch: false }
  );

  onboardingDataFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message);
        })
      ),
    { dispatch: false }
  );

  updateUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserProfile),
      exhaustMap(({ guid, postdata }) => {
        return this.teamsService.updateTeamsProfile(guid, postdata).pipe(
          map((user: any) => {
            return updateUserProfileSuccess({ user: user });
          }),
          catchError((error) => of(updateUserProfileFailure({ error })), // Closing parenthesis added here
          ));
      })
    )
  );

  updateUserProfileFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateUserProfileFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message ? error.error.message : "Something went wrong. Please check again");
        })
      ),
    { dispatch: false }
  );

  constructor(
    @Inject(Actions) private actions$: Actions,
    private AuthenticationService: AuthenticationService,
    private toastifyService: ToastifyService,
    private router: Router,
    private teamsService: TeamService,
    private genericService: GenericService,
    private afs: AngularFirestore) { }

}